import { ApiError } from '@api/api-client';
import { createCategory, type CreateCategoryDetailDTO } from '@api/categories-api';
import type { CountryResponse } from '@api/countries-api';
import ConfirmationModal from '@components/confirmation-modal';
import FieldError from '@components/field-error';
import Modal from '@components/modal';
import Toggle from '@components/toggle';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCountries } from '@hooks/use-countries';
import { type CategoryNameFormData, categoryNameSchema } from '@validation/schemas';
import { Check, ChevronDown, ChevronRight, Globe, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { sileo } from 'sileo';

const MAX_MARKETPLACE_IDS = 10;

type MarketplaceKey = `marketplaceId${number}`;

interface CountryDetail {
  name: string;
  [key: MarketplaceKey]: string;
}

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function createEmptyDetail(): CountryDetail {
  const detail: CountryDetail = { name: '' };
  for (let i = 1; i <= MAX_MARKETPLACE_IDS; i++) {
    detail[`marketplaceId${i}` as MarketplaceKey] = '';
  }
  return detail;
}

function isCountryFilled(detail: CountryDetail | undefined): boolean {
  if (!detail) return false;
  if (detail.name.trim()) return true;
  for (let i = 1; i <= MAX_MARKETPLACE_IDS; i++) {
    if ((detail[`marketplaceId${i}` as MarketplaceKey] ?? '').trim()) return true;
  }
  return false;
}

export default function CreateCategoryModal({
  isOpen,
  onClose,
}: CreateCategoryModalProps): React.JSX.Element {
  const { countries } = useCountries();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isClinic, setIsClinic] = useState(false);
  const [isGlobal, setIsGlobal] = useState(false);
  const [externalId, setExternalId] = useState('');
  const [detailsByCountry, setDetailsByCountry] = useState<Record<string, CountryDetail>>({});
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set());

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm<CategoryNameFormData>({
    resolver: zodResolver(categoryNameSchema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ name: '' });
      setIsClinic(false);
      setIsGlobal(false);
      setExternalId('');
      setExpandedCountries(new Set());
      const initial: Record<string, CountryDetail> = {};
      countries.forEach((c: CountryResponse) => {
        initial[c.id] = createEmptyDetail();
      });
      setDetailsByCountry(initial);
    }
  }, [isOpen, countries, reset]);

  const filledCount = countries.filter((c) => isCountryFilled(detailsByCountry[c.id])).length;

  const toggleCountry = (countryId: string): void => {
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      if (next.has(countryId)) {
        next.delete(countryId);
      } else {
        next.add(countryId);
      }
      return next;
    });
  };

  const updateDetail = (countryId: string, field: string, value: string): void => {
    setDetailsByCountry((prev) => {
      const current = prev[countryId] ?? createEmptyDetail();
      return { ...prev, [countryId]: { ...current, [field]: value } };
    });
  };

  const onValidName = (): void => {
    const missing = countries.filter((c) => {
      const detail = detailsByCountry[c.id];
      if (!isCountryFilled(detail)) return false;
      return !(detail?.marketplaceId1 ?? '').trim();
    });

    if (missing.length > 0) {
      const names = missing.map((c) => c.name).join(', ');
      sileo.error({ title: `Marketplace ID 1 is required for: ${names}` });
      return;
    }

    setIsConfirmOpen(true);
  };

  const handleConfirm = async (): Promise<void> => {
    const name = getValues('name');
    const details: CreateCategoryDetailDTO[] = countries.flatMap((c: CountryResponse) => {
      const detail = detailsByCountry[c.id];
      if (!isCountryFilled(detail)) return [];

      const entry: Record<string, string | number> = {
        countryId: c.id,
        name: detail?.name?.trim() || name.trim(),
        marketplaceId1: Number((detail?.marketplaceId1 ?? '').trim()),
      };

      for (let i = 2; i <= MAX_MARKETPLACE_IDS; i++) {
        const key = `marketplaceId${i}` as MarketplaceKey;
        const value = (detail?.[key] ?? '').trim();
        if (value) entry[key] = Number(value);
      }

      return [entry as unknown as CreateCategoryDetailDTO];
    });

    try {
      const parsedExternalId = externalId.trim() ? Number(externalId.trim()) : undefined;
      await createCategory({
        name: name.trim(),
        externalId: parsedExternalId,
        isClinic,
        isGlobal,
        details,
      });

      sileo.success({ title: 'Category created successfully' });
      setIsConfirmOpen(false);
      onClose();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to create category';
      sileo.error({ title: message });
      setIsConfirmOpen(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="New category"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleSubmit(onValidName)()}
            >
              <Plus size={16} />
              Create category
            </button>
          </>
        }
      >
        <form className="modal-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label htmlFor="cat-name" className="form-label">
              Name
            </label>
            <input
              id="cat-name"
              type="text"
              className={`form-input${errors.name ? ' form-input--error' : ''}`}
              placeholder="e.g. Cardiology"
              {...register('name')}
            />
            <FieldError error={errors.name} />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-4)',
              marginBottom: 'var(--space-4)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <label
                htmlFor="cat-external-id"
                className="form-label"
                style={{ margin: 0, whiteSpace: 'nowrap' }}
              >
                CSV ID
              </label>
              <input
                id="cat-external-id"
                type="number"
                className="form-input"
                placeholder="Optional"
                style={{ width: 120 }}
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
              />
            </div>
            <Toggle id="cat-is-clinic" checked={isClinic} onChange={setIsClinic} label="Clinic" />
            <Toggle id="cat-is-global" checked={isGlobal} onChange={setIsGlobal} label="Global" />
          </div>

          {countries.length > 0 && (
            <div className="country-details-section">
              <div className="country-details-section__header">
                <div className="country-details-section__title">
                  <Globe size={16} />
                  <span>Country details</span>
                  {filledCount > 0 && (
                    <span className="country-details-section__count">{filledCount} customized</span>
                  )}
                </div>
                <p className="country-details-section__hint">
                  Expand each country to set its name and marketplace IDs. Only countries with data
                  will be included.
                </p>
              </div>

              <div className="country-accordion">
                {countries.map((country: CountryResponse) => {
                  const isExpanded = expandedCountries.has(country.id);
                  const isFilled = isCountryFilled(detailsByCountry[country.id]);

                  return (
                    <div
                      key={country.id}
                      className={`country-accordion__item${isExpanded ? ' country-accordion__item--expanded' : ''}`}
                    >
                      <button
                        type="button"
                        className="country-accordion__trigger"
                        onClick={() => toggleCountry(country.id)}
                        aria-expanded={isExpanded}
                      >
                        <span className="country-accordion__chevron">
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </span>
                        <span className="country-accordion__country-name">{country.name}</span>
                        {isFilled && (
                          <span className="country-accordion__filled-indicator">
                            <Check size={12} />
                          </span>
                        )}
                      </button>

                      {isExpanded && (
                        <div className="country-accordion__content">
                          <div className="form-group">
                            <label
                              htmlFor={`cat-detail-name-${country.id}`}
                              className="form-label form-label--xs"
                            >
                              Localized name
                            </label>
                            <input
                              id={`cat-detail-name-${country.id}`}
                              type="text"
                              className="form-input"
                              placeholder={getValues('name') || 'Same as base name'}
                              value={detailsByCountry[country.id]?.name ?? ''}
                              onChange={(e) => updateDetail(country.id, 'name', e.target.value)}
                            />
                          </div>
                          <div className="country-accordion__mp-row">
                            {Array.from({ length: MAX_MARKETPLACE_IDS }, (_, i) => i + 1).map(
                              (n) => {
                                const key = `marketplaceId${n}` as MarketplaceKey;
                                const isRequired = n === 1;
                                return (
                                  <div key={key} className="form-group">
                                    <label
                                      htmlFor={`cat-detail-mp${n}-${country.id}`}
                                      className="form-label form-label--xs"
                                    >
                                      Marketplace ID {n}
                                      {isRequired ? ' *' : ''}
                                    </label>
                                    <input
                                      id={`cat-detail-mp${n}-${country.id}`}
                                      type="text"
                                      className="form-input"
                                      placeholder={isRequired ? 'Required' : 'Optional'}
                                      value={detailsByCountry[country.id]?.[key] ?? ''}
                                      onChange={(e) =>
                                        updateDetail(country.id, key, e.target.value)
                                      }
                                    />
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => void handleConfirm()}
        title="Create category"
        message="Are you sure you want to create this category?"
        confirmLabel="Create"
      />
    </>
  );
}
